import React, { useMemo } from "react";
import { Popover } from "antd";
import { useTranslation } from "react-i18next";
import { usePeerConnection } from "features/robot-teleop/hooks/usePeerConnection";
import { usePeerStats } from "features/robot-teleop/hooks/usePeerStats";
import { PEER_STATES } from "features/robot-teleop/webrtc/peerStates";
import Sparkline from "./Sparkline";
import PeerStatsDetails from "./PeerStatsDetails";
import { dotStatus, gradeColor, gradeText, GRADE_LETTER, fmtMs, fmtNum } from "./linkPresentation";

const SPARK_SAMPLES = 30;

/**
 * PeerTileBadge — always-visible overlay at the top-left of a peer tile:
 * connection dot · peer id · quality grade · RTT · jitter · fps · jitter sparkline.
 * Click opens a popover with the full live metric table. Compact variant
 * (audio-only chips) shows dot · id · grade · RTT.
 *
 * Only this component re-renders on a stats tick (it owns the subscription).
 */
const PeerTileBadge = ({ peerId, compact = false }) => {
  const { t } = useTranslation();
  const { state, connectionState, retryCount, cameras, isControlChannel } = usePeerConnection(peerId);
  const { sample, history, health, grade, healthSince } = usePeerStats(peerId, { historySize: SPARK_SAMPLES });
  const expectsVideo = cameras.length > 0;

  const dot = dotStatus({ state, connectionState, health, grade, retryCount, expectsVideo }, t);
  const jitterSeries = useMemo(
    () => history.map((s) => s?.video?.jitterMs ?? s?.audio?.jitterMs ?? null),
    [history]
  );

  const rtt = sample?.rttMs ?? null;
  const jitter = sample?.video?.jitterMs ?? sample?.audio?.jitterMs ?? null;
  const fps = sample?.video?.fps ?? null;
  const connected = state === PEER_STATES.CONNECTED;
  const stalledFor = dot.cls === "stalled" && healthSince ? Math.max(0, Math.round((Date.now() - healthSince) / 1000)) : null;

  const title = [
    `${peerId}${isControlChannel ? ` ${t("teleop.peer.badge.controlSuffix")}` : ""} · ${dot.text}`,
    rtt != null ? t("teleop.peer.metric.rttText", { value: fmtMs(rtt) }) : null,
    jitter != null ? t("teleop.peer.metric.jitterText", { value: fmtMs(jitter, 1) }) : null,
    fps != null ? t("teleop.peer.metric.fpsText", { value: fmtNum(fps, 1) }) : null,
    t("teleop.peer.badge.clickForDetails"),
  ].filter(Boolean).join(" · ");

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      destroyOnHidden
      content={<PeerStatsDetails peerId={peerId} />}
      title={t("teleop.peer.badge.detailsTitle", { peerId })}
    >
      <div
        className={`peer-badge ${compact ? "compact" : ""} health-${health}`}
        title={title}
        role="button"
        tabIndex={0}
        data-peer-id={peerId}
      >
        <span className={`dot ${dot.cls}`} />
        <span className="peer-id">{peerId}</span>
        {isControlChannel && <span className="role">{t("teleop.peer.badge.control")}</span>}
        {connected && grade && (
          <span className={`grade ${grade}`} title={t("teleop.peer.quality.grade", { text: gradeText(grade, t) })}>
            {GRADE_LETTER[grade]}
          </span>
        )}
        {connected && !dot.alert && (
          <>
            <span className="metric rtt" title={t("teleop.peer.metric.rtt")}><b>{fmtMs(rtt)}</b> {t("teleop.peer.metric.ms")}</span>
            <span className="metric jitter" title={t("teleop.peer.metric.videoJitter")}>⌇ <b>{fmtMs(jitter, 1)}</b> {t("teleop.peer.metric.ms")}</span>
            {expectsVideo && (
              <span className={`metric fps ${fps != null && fps < 5 ? "bad" : ""}`} title={t("teleop.peer.metric.fps")}>
                <b>{fmtNum(fps)}</b> {t("teleop.peer.metric.fpsUnit")}
              </span>
            )}
          </>
        )}
        {(!connected || dot.alert) && (
          <span className={`state-text ${dot.alert ? "alert" : ""}`}>
            {dot.text}{stalledFor != null ? ` ${stalledFor}s` : ""}
          </span>
        )}
        {connected && !compact && (
          <Sparkline
            values={jitterSeries}
            width={48}
            height={14}
            max={100}
            warnAt={30}
            lastColor={gradeColor(grade)}
            title={t("teleop.peer.metric.jitterSpark")}
          />
        )}
      </div>
    </Popover>
  );
};

export default React.memo(PeerTileBadge);
